import { Cache } from 'aws-amplify';
import { Auth } from '@aws-amplify/auth';
import { Account } from '../../accounts/types';
import { checkAccountSession } from '../../account-sessions/services/AccountSessionService';
import returnToDomainNames from '../../../config/return-to-domain-names';
import { 
    OAuth2Params, 
    OAuth2TokenInfo, 
    ValidationResult, 
    OAuth2AuthorizationResult,
    OAuth2AuthorizationCache 
} from '../types';
import { OAuth2Errors, mapErrorToOAuth2Error } from '../utils/errorHandler';
import { ActivityEventsService } from '../../debug-mode/activity-events/services/ActivityEventsService';

export class OAuth2Service {
    
    /**
     * Validates redirect URL against allowed domains
     */
    validateRedirectUrl(redirectUrl: string | null): ValidationResult {
        if (!redirectUrl) {
            return { isValid: false, error: 'Redirect URL is required' };
        }

        try {
            const url = new URL(redirectUrl);
            const allowedDomains = returnToDomainNames;
            
            const isAllowed = allowedDomains.some(domain => 
                url.hostname === domain || url.hostname.endsWith(`.${domain}`)
            );
            
            if (!isAllowed) {
                return { isValid: false, error: `Invalid redirect URL domain: ${url.hostname}` };
            }
            
            return { isValid: true };
        } catch (error) {
            return { isValid: false, error: 'Invalid redirect URL format' };
        }
    }

    /**
     * Finds the best account for OAuth2 authorization
     * First tries exact client_id match, then falls back to any active session
     */
    async findBestAccountForOAuth2(accounts: Account[], clientId: string | null): Promise<Account | undefined> {
        if (!clientId) return undefined;
        
        // First try exact client_id match
        const exactMatch = accounts.find(acc => acc.appClientId === clientId);
        if (exactMatch) {
            // Check if this account has an active session
            const sessionInfo = await checkAccountSession(exactMatch);
            if (sessionInfo.isActive && !sessionInfo.isExpired) {
                ActivityEventsService.add(
                    'OAuth2 Exact Client Match Found',
                    `Found exact match for client_id: ${clientId} with active session`,
                    exactMatch.emailAddress
                );
                return exactMatch;
            }
        }
        
        // No exact match with active session, find any account with active session
        ActivityEventsService.add(
            'OAuth2 Client ID Mismatch - Searching Active Sessions',
            `No active session for requested client_id: ${clientId}, searching for any active session`,
            undefined
        );
        
        for (const account of accounts) {
            const sessionInfo = await checkAccountSession(account);
            if (sessionInfo.isActive && !sessionInfo.isExpired) {
                ActivityEventsService.add(
                    'OAuth2 Using Alternative Active Session',
                    `Using active session from client_id: ${account.appClientId} instead of requested: ${clientId}`,
                    account.emailAddress
                );
                return account;
            }
        }
        
        ActivityEventsService.add(
            'OAuth2 No Active Sessions Found',
            `No active sessions found for any account (requested client_id: ${clientId})`,
            undefined
        );
        
        return undefined;
    }

    /**
     * Validates nonce in ID token (if provided)
     * 
     * SECURITY NOTE: Currently disabled to preserve SSO functionality
     * in multi-application environments. See docs/nonce-quick-fix.md
     */
    validateNonce(_idToken: string, expectedNonce: string | null): ValidationResult {
        if (!expectedNonce) {
            return { isValid: true }; // Nonce is optional
        }

        // TEMPORARY FIX: Disable nonce validation to prevent SSO breakage
        // TODO: Implement proper session-aware nonce validation
        // See: src/features/oauth2/docs/nonce-quick-fix.md for details
        console.log('NONCE VALIDATION DISABLED: Preserving SSO functionality');
        return { isValid: true };

        /* ORIGINAL IMPLEMENTATION (DISABLED):
        try {
            // Decode JWT payload (basic validation - in production use proper JWT library)
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            
            if (payload.nonce !== expectedNonce) {
                return { isValid: false, error: 'Nonce mismatch in ID token' };
            }
            
            return { isValid: true };
        } catch (error) {
            return { isValid: false, error: 'Invalid ID token format' };
        }
        */
    }

    /**
     * Builds OAuth2 response URL with tokens
     */
    buildAuthorizationResponseUrl(
        redirectUrl: string, 
        tokens: OAuth2TokenInfo, 
        state: string | null
    ): string {
        return `${redirectUrl}#id_token=${tokens.idToken}&access_token=${tokens.accessToken}&token_type=${tokens.tokenType}&expires_in=${tokens.expiresIn}&state=${state || ''}`;
    }

    /**
     * Extracts tokens from Cognito session
     */
    extractTokensFromSession(session: any): OAuth2TokenInfo {
        return {
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            tokenType: 'Bearer',
            expiresIn: 3600
        };
    }

    /**
     * Caches authorization parameters
     */
    cacheAuthorizationParams(params: OAuth2AuthorizationCache): void {
        ActivityEventsService.add(
            'OAuth2 Parameters Cached',
            `Cached OAuth2 params for client_id: ${params.clientId}`,
            undefined
        );
        Cache.setItem('authorization', params);
    }

    /**
     * Clears authorization cache
     */
    clearAuthorizationCache(): void {
        ActivityEventsService.add(
            'OAuth2 Cache Cleared',
            'OAuth2 authorization parameters cleared from cache',
            undefined
        );
        Cache.removeItem('authorization');
    }

    /**
     * Performs OAuth2 authorization flow
     */
    async authorize(accounts: Account[], params: OAuth2Params): Promise<OAuth2AuthorizationResult> {
        try {
            const { redirectUrl, state, clientId, nonce } = params;

            // Smart account selection: try exact match first, then any active session
            const account = await this.findBestAccountForOAuth2(accounts, clientId);
            if (!account) {
                ActivityEventsService.add(
                    'OAuth2 No Suitable Account Found',
                    `No account with active session found (requested client_id: ${clientId})`,
                    undefined
                );
                const error = OAuth2Errors.unauthorizedClient('No account with active session found', state || undefined);
                return { success: false, error: error.toErrorResponse() };
            }

            // Validate redirect URL
            const validation = this.validateRedirectUrl(redirectUrl);
            if (!validation.isValid) {
                ActivityEventsService.add(
                    'OAuth2 Redirect URL Validation Failed',
                    `Invalid redirect URL: ${redirectUrl} - ${validation.error}`,
                    account?.emailAddress
                );
                const error = OAuth2Errors.invalidRequest(validation.error, state || undefined);
                return { success: false, error: error.toErrorResponse() };
            }

            // Check session (already checked in findBestAccountForOAuth2, but verify again)
            const sessionInfo = await checkAccountSession(account);
            if (!sessionInfo.isActive || sessionInfo.isExpired || !sessionInfo.session) {
                ActivityEventsService.add(
                    'OAuth2 Session Re-validation Failed',
                    `Session became invalid during OAuth2 flow for account: ${account.emailAddress}`,
                    account.emailAddress
                );
                const error = OAuth2Errors.accessDenied('Session expired or invalid', state || undefined);
                return { success: false, error: error.toErrorResponse() };
            }

            // Extract tokens
            const tokens = this.extractTokensFromSession(sessionInfo.session);
            
            // Validate nonce if provided
            const nonceValidation = this.validateNonce(tokens.idToken, nonce ?? null);
            if (!nonceValidation.isValid) {
                const error = OAuth2Errors.invalidRequest(nonceValidation.error, state || undefined);
                return { success: false, error: error.toErrorResponse() };
            }
            
            const responseUrl = this.buildAuthorizationResponseUrl(redirectUrl || '', tokens, state);

            return {
                success: true,
                tokens,
                redirectUrl: responseUrl
            };

        } catch (error) {
            const oauth2Error = mapErrorToOAuth2Error(error, params.state || undefined);
            return { 
                success: false, 
                error: oauth2Error.toErrorResponse() 
            };
        }
    }

    /**
     * Signs out user globally
     */
    async signOut(global: boolean = false): Promise<void> {
        ActivityEventsService.add(
            'OAuth2 Sign Out',
            `OAuth2 flow triggered sign out (global: ${global})`,
            undefined
        );
        await Auth.signOut({ global });
    }

    /**
     * Redirects to authorization response URL
     */
    redirectToApp(responseUrl: string): void {
        this.clearAuthorizationCache();
        window.location.assign(responseUrl);
    }
}